import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { enableNotificationsFromClick } from '@/components/layout/enable-notifications-prompt';
import { useNotificationPermission } from '@/hooks/use-notification-permission';
import { showSystemNotification } from '@/lib/notifications/system-notifications';
import { cn } from '@/lib/utils';

const STATUS = {
  granted: {
    label: 'On',
    tone: 'text-emerald-600 dark:text-emerald-400',
    description:
      "New messages and announcements pop up while Clarimed is open in this browser. Nothing showing? Allow your browser in your computer's notification settings and check Focus / Do Not Disturb.",
  },
  default: {
    label: 'Off',
    tone: 'text-muted-foreground',
    description: 'Turn them on to get alerts for new messages and announcements.',
  },
  denied: {
    label: 'Blocked',
    tone: 'text-rose-600 dark:text-rose-400',
    description:
      "This site is blocked from showing notifications. Allow them in your browser's site settings (the icon next to the address), then come back to this tab.",
  },
  insecure: {
    label: 'Unavailable',
    tone: 'text-amber-600 dark:text-amber-400',
    description: 'Browsers only allow notifications on https:// addresses (or localhost).',
  },
  unsupported: {
    label: 'Unavailable',
    tone: 'text-amber-600 dark:text-amber-400',
    description: "This browser can't show desktop notifications.",
  },
};

// No tag, so every test alerts again instead of silently replacing the previous one.
function sendTestNotification() {
  const failed = () => toast.error("The browser couldn't show a notification.");
  const notification = showSystemNotification({
    title: 'Clarimed notifications are on',
    body: 'New messages will show up like this.',
  });
  if (!notification) {
    failed();
    return;
  }
  notification.addEventListener('error', failed);
}

/**
 * Web-only (Electron's permission is granted by the main process): this browser's notification
 * permission, the way to enable it (the sidebar prompt can be dismissed), and what to do when it
 * is blocked. A page can't revoke a granted permission, so there is no off switch — Do not
 * disturb and per-chat mute are the in-app controls.
 */
export function NotificationsCard() {
  const permission = useNotificationPermission();
  const status = STATUS[permission] || STATUS.unsupported;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl">Notifications</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Applies to this browser only. Do not disturb and muted chats still apply, and nothing arrives
          while Clarimed is locked or its tab is closed.
        </p>
        <div className="flex items-center justify-between gap-4 py-1">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">
              Desktop notifications{' '}
              <span className={cn('ml-1 text-xs font-semibold', status.tone)}>{status.label}</span>
            </p>
            <p className="text-xs text-muted-foreground">{status.description}</p>
          </div>
          {permission === 'default' ? (
            <Button type="button" size="sm" className="shrink-0" onClick={() => void enableNotificationsFromClick()}>
              Enable notifications
            </Button>
          ) : null}
          {permission === 'granted' ? (
            <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={sendTestNotification}>
              Send test notification
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
